/** tm/legal/LegalStageTimeline.tsx — 「진행 단계」: 한 법령안이 입법예고 → 법제처 심사 → 차관회의 → 국무회의 의결 → (국회 제출) → 공포 → 시행 으로
 *  어디까지 왔는지 (GET /cabinet/progress — 법제처 정부입법현황 기준). 입법예고·국무회의·국회 법안·법령 개정 상세에 같이 붙는다.
 *  단계마다 모아 둔 글이 있으면 「보기 ›」로 그 글을 연다 (지금 보는 글은 표시만). 이어진 법령안이 없으면 아무것도 그리지 않는다. */
import { useEffect, useState } from 'react';
import { C, T, R } from '../components/ui';
import { getProgress } from './legalApi';
import { shortDate, daysUntil, safeHttp, MUTED } from './legalTheme';
import { DETAIL_MAX } from './LegalDetailParts';
import type { GovProgress, ItemKind, ProgressStep, Selection } from './legalTypes';

interface Props {
  kind: Exclude<ItemKind, 'news'>; id: number;
  onOpen?: (s: NonNullable<Selection>) => void;
  /** 국무회의 상세처럼 원문 버튼이 따로 있으면 출처 줄의 원문 링크를 뺀다 */
  hideSource?: boolean;
}

const LINK_LABEL: Partial<Record<ItemKind, string>> = { notice: '입법예고 보기', cabinet: '국무회의 보기', bill: '국회 법안 보기', legal: '공포 법령 보기' };

/** 단계 옆 회색 설명 (기간 · 회차 · 공포번호 · 의안번호 · 국회 상태 · 시행 D-n) */
function metaOf(s: ProgressStep): string {
  const parts: string[] = [];
  if (s.date) parts.push(s.end_date ? `${shortDate(s.date)} ~ ${shortDate(s.end_date).slice(5)}` : shortDate(s.date));
  if (s.no) parts.push(`${s.no}회`);
  if (s.prom_no) parts.push(s.prom_no);
  if (s.bill_no) parts.push(`의안 ${s.bill_no}`);
  if (s.note) parts.push(`국회 ${s.note}`);
  if (s.stage === '시행') {
    const d = daysUntil(s.date);
    if (d != null) parts.push(d > 0 ? `시행 D-${d}` : d === 0 ? '오늘 시행' : '시행 중');
  }
  return parts.join(' · ');
}

export default function LegalStageTimeline({ kind, id, onOpen, hideSource }: Props) {
  const [p, setP] = useState<GovProgress | null>(null);
  useEffect(() => {
    let alive = true;
    setP(null);
    getProgress(kind, id).then(r => { if (alive) setP(r && Array.isArray(r.steps) && r.steps.length ? r : null); }, () => undefined);
    return () => { alive = false; };
  }, [kind, id]);
  if (!p) return null;
  const src = safeHttp(p.gov.detail_url);
  const last = p.steps.length - 1;

  return (
    <section aria-label="진행 단계" style={{ border: `1px solid ${C.border}`, borderRadius: R.md, padding: '10px 14px 8px', marginBottom: 12, maxWidth: DETAIL_MAX, background: C.white }}>
      <h4 style={{ margin: '0 0 8px', fontSize: T.md, color: C.sub, display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
        진행 단계 <span style={{ fontWeight: 400, color: MUTED, fontSize: T.sm }}>정부 법령안 「{p.gov.title}」</span>
      </h4>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {p.steps.map((s, i) => {
          const self = !!s.link && s.link.kind === kind && s.link.id === id;
          const future = s.stage === '시행' && (daysUntil(s.date) ?? -1) > 0;
          const dot = self ? C.accent : future ? C.white : C.green;
          const canOpen = !!s.link && !self && !!onOpen;
          return (
            <li key={`${s.stage}${i}`} aria-current={self ? 'step' : undefined} style={{ display: 'grid', gridTemplateColumns: '16px 1fr auto', columnGap: 10, alignItems: 'start', position: 'relative', paddingBottom: i === last ? 2 : 10 }}>
              {i < last && <span aria-hidden="true" style={{ position: 'absolute', left: 6, top: 16, bottom: 0, width: 2, background: C.border }} />}
              <span aria-hidden="true" style={{ width: 12, height: 12, borderRadius: 6, marginTop: 5, marginLeft: 1, background: dot, border: `2px solid ${self ? C.accent : future ? C.borderDk : C.green}`, boxSizing: 'border-box', position: 'relative' }} />
              <span style={{ minWidth: 0, lineHeight: 1.5 }}>
                <span style={{ fontSize: 14, fontWeight: self ? 700 : 600, color: self ? C.accent : future ? C.sub : C.text, marginRight: 8 }}>{s.label}</span>
                {self && <span style={{ fontSize: T.sm, fontWeight: 600, color: C.accent, background: C.accentLt, borderRadius: 4, padding: '0 5px', marginRight: 8, whiteSpace: 'nowrap' }}>지금 보는 글</span>}
                <span style={{ fontSize: T.md, color: C.sub, fontVariantNumeric: 'tabular-nums', wordBreak: 'keep-all' }}>{metaOf(s)}</span>
              </span>
              {canOpen && s.link
                ? <button type="button" className="lw-f" onClick={() => s.link && onOpen && onOpen({ kind: s.link.kind, id: s.link.id })} style={{
                  border: 0, background: 'transparent', color: C.accent, fontSize: T.sm, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', padding: '2px 0', whiteSpace: 'nowrap',
                }}>{LINK_LABEL[s.link.kind] || '보기'} ›</button>
                : <span />}
            </li>
          );
        })}
      </ol>
      <div style={{ fontSize: T.sm, color: MUTED, borderTop: `1px solid ${C.bg}`, marginTop: 6, paddingTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span>출처: 법제처 국민참여입법센터 「정부입법현황」{p.gov.status ? ` · 현재 ${p.gov.status}` : ''}</span>
        {!hideSource && src && <a className="lw-f" href={src} target="_blank" rel="noopener noreferrer" style={{ color: C.accent, fontWeight: 600, textDecoration: 'none' }}>원문 ›</a>}
      </div>
    </section>
  );
}
